import { useCallback, useEffect, useState, useRef } from 'react'
import { FormControl, Grid, MenuItem, Paper, Select, Typography } from '@material-ui/core'
import { useSelector } from 'react-redux'
import { getProvider } from '../../api/providers.service'
import { getProviderReports } from '../../api/providerReports.service'
import ResourceList from '../../components/Resources/ResourceList'
import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'

Chart.register(ChartDataLabels)

const valueOk = 100
const valueKo = 75
const colorGap = 10
const backgroundColorOk = '#2FC202B9'
const backgroundColorKo = '#FF0000B9'
const borderColorOk = '#6AE0AEFF'
const borderColorKo = '#FFB0AEFF'
const chartConfig = {
  type: 'bar',
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 0,
          boxHeight: 0,
          font: {
            size: 16,
            weight: 'bold'
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (item) => ` ${item.raw} %`
        }
      },
      datalabels: {
        backgroundColor: '#0000000d',
        anchor: 'end',
        align: 'end',
        formatter: (value) => (value != null ? `${value} %` : 'SIN DATOS')
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  },
  data: {
    labels: [],
    datasets: [
      {
        pointHoverBackgroundColor: '#fff',
        borderWidth: 2,
        backgroundColor: [],
        borderColor: [],
        data: []
      }
    ]
  },
  plugins: [
    {
      id: 'legendMarginBottom',
      afterInit: (chart) => {
        const fitValue = chart.legend.fit
        chart.legend.fit = function fit() {
          fitValue.bind(chart.legend)()
          return (this.height += 25)
        }
      }
    }
  ]
}
const months = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
]
const allYears = 0

const ReportsView = () => {
  const getColor = useCallback((value, colorOk, colorKo) => {
    if (!value) return null
    if (value <= valueKo) return colorKo
    if (value >= valueOk) return colorOk

    const valueKoWithGap = valueKo - colorGap
    const valueOkWithGap = valueOk + colorGap
    const normalizedFactor =
      (Math.floor(value) - valueKoWithGap) / (valueOkWithGap - valueKoWithGap)

    const colorRegex = /.{2}/g
    const okRGBA = colorOk.substring(1).match(colorRegex)
    const koRGBA = colorKo.substring(1).match(colorRegex)
    let color = '#'
    for (let index = 0; index < 4; index++) {
      const ok = parseInt(okRGBA[index], 16)
      const ko = parseInt(koRGBA[index], 16)
      const lerp = ko + (ok - ko) * normalizedFactor
      const hex = Math.round(Math.max(0, Math.min(255, lerp)))
      color += hex.toString(16).padStart(2, '0')
    }
    return color
  }, [])

  const ordersChart = useRef(null)
  const articlesChart = useRef(null)
  const ordersChartCanvas = useRef(null)
  const articlesChartCanvas = useRef(null)

  const [chartData, setChartData] = useState([])
  const [years, setYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('')
  const [ordersInDatePercentage, setOrdersInDatePercentage] = useState(0)
  const [articlesReceivedPercentage, setArticlesReceivedPercentage] = useState(0)
  const [reports, setReports] = useState({})
  const providerSelected = useSelector((state) => state.provider)

  const getProviderData = useCallback(() => {
    const headers = {
      skipLog: true
    }
    getProvider(providerSelected.id, headers).then(({ data }) => {
      setReports(data.reports)
    })
  }, [providerSelected.id])

  const getProviderReportsData = useCallback(() => {
    getProviderReports(providerSelected.id).then(({ data }) => {
      if (!data || data.length === 0) {
        const currentYear = new Date().getFullYear()
        setYears([currentYear])
        setSelectedYear(currentYear)
        return
      }

      const newChartData = data.map((item) => {
        const date = new Date(item.date)
        return {
          year: date.getFullYear(),
          month: date.getMonth(),
          item: item
        }
      })
      setChartData(newChartData)
      const newYears = [...new Set(newChartData.map((d) => d.year))].sort()
      setYears(newYears)
      setSelectedYear(newYears[newYears.length - 1])
    })
  }, [providerSelected.id])

  const createCharts = useCallback(() => {
    let ordersChartConfig = { ...chartConfig }
    let articlesChartConfig = { ...chartConfig }

    const setChartConfigData = (config, label, value) => {
      const data = { ...config.data }
      data.labels = [...data.labels, label]

      const dataset = { ...data.datasets[0] }
      dataset.backgroundColor = [
        ...dataset.backgroundColor,
        getColor(value, backgroundColorOk, backgroundColorKo)
      ]
      dataset.borderColor = [...dataset.borderColor, getColor(value, borderColorOk, borderColorKo)]
      dataset.data = [...dataset.data, value != null ? value.toFixed(2) : null]
      data.datasets = [dataset]

      config.data = data
    }
    let aggregatedChartData = []
    if (selectedYear === allYears) {
      for (let year of years) {
        const yearChartData = chartData.filter((d) => d.year === year)
        setChartConfigData(
          ordersChartConfig,
          year,
          yearChartData.length !== 0
            ? yearChartData
                .map((d) => d.item.ordersInDatePercentage / months.length)
                .reduce((previous, current) => previous + current, 0)
            : null
        )
        setChartConfigData(
          articlesChartConfig,
          year,
          yearChartData.length !== 0
            ? yearChartData
                .map((d) => d.item.articlesReceivedPercentage / months.length)
                .reduce((previous, current) => previous + current, 0)
            : null
        )
      }
      aggregatedChartData = chartData
    } else {
      for (let month = 0; month < months.length; month++) {
        const label = `${months[month]} ${selectedYear}`
        const monthChartData = chartData.find((d) => d.year === selectedYear && d.month === month)
        setChartConfigData(
          ordersChartConfig,
          label,
          monthChartData ? monthChartData.item.ordersInDatePercentage : null
        )
        setChartConfigData(
          articlesChartConfig,
          label,
          monthChartData ? monthChartData.item.articlesReceivedPercentage : null
        )
      }
      aggregatedChartData = chartData.filter((d) => d.year === selectedYear)
    }

    const totalOrdersSum = aggregatedChartData
      .map((d) => d.item.totalOrders)
      .reduce((previous, current) => previous + current, 0)
    const ordersInDateSum = aggregatedChartData
      .map((d) => d.item.ordersInDate)
      .reduce((previous, current) => previous + current, 0)
    const ordersInDatePercentageSum = (ordersInDateSum * 100) / totalOrdersSum
    setOrdersInDatePercentage(
      (isNaN(ordersInDatePercentageSum) ? 0 : ordersInDatePercentageSum).toFixed(2)
    )

    const articlesReceivedPercentageSum = aggregatedChartData
      .filter((d) => d.item.articlesReceivedPercentage !== 0)
      .map((d) => d.item.articlesReceivedPercentage)
      .reduce((previous, current, _, array) => previous + current / array.length, 0)
      .toFixed(2)
    setArticlesReceivedPercentage(articlesReceivedPercentageSum)

    ordersChartConfig.data.datasets[0].label = 'Porcentaje de pedidos en fecha'
    ordersChart.current = new Chart(ordersChartCanvas.current, ordersChartConfig)

    articlesChartConfig.data.datasets[0].label = 'Porcentaje de artículos recibidos'
    articlesChart.current = new Chart(articlesChartCanvas.current, articlesChartConfig)
  }, [years, selectedYear, chartData, getColor])

  useEffect(() => {
    getProviderData()
    getProviderReportsData()
  }, [providerSelected.id, getProviderData, getProviderReportsData])

  useEffect(() => {
    if (selectedYear === '') return
    createCharts()
    return () => {
      if (ordersChart.current) ordersChart.current.destroy()
      if (articlesChart.current) articlesChart.current.destroy()
    }
  }, [selectedYear, createCharts])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl>
          <Select
            value={selectedYear}
            onChange={(ev) => setSelectedYear(ev.target.value)}
            style={{ width: '100%', minWidth: 150 }}
          >
            <MenuItem value={allYears}>Todos los años</MenuItem>
            {years.map((year, indexOption) => (
              <MenuItem key={`years-${indexOption}`} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={9}>
        <Paper style={{ padding: 16, height: '300px' }}>
          <canvas ref={ordersChartCanvas}></canvas>
        </Paper>
      </Grid>
      <Grid item xs={3}>
        <Paper style={{ padding: 16, height: '300px' }}>
          <Typography variant="h5" align="center">
            Porcentaje de pedidos en fecha
          </Typography>
          <Typography variant="h2" align="center">
            {ordersInDatePercentage} %
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={9}>
        <Paper style={{ padding: 16, height: '300px' }}>
          <canvas ref={articlesChartCanvas}></canvas>
        </Paper>
      </Grid>
      <Grid item xs={3}>
        <Paper style={{ padding: 16, height: '300px' }}>
          <Typography variant="h5" align="center">
            Porcentaje de artículos recibidos
          </Typography>
          <Typography variant="h2" align="center">
            {articlesReceivedPercentage} %
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper style={{ padding: 16 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography color="secondary">REPORTES</Typography>
            </Grid>
            <Grid item xs={12} md={8} className={reports ? 'mx-auto' : ''}>
              <ResourceList resources={reports} noDataLabel="No hay reportes" />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  )
}

export default ReportsView
